import React, { useRef, useState } from 'react';
import { orders as ordersApi } from '../utils/api';
import { fmtDate } from '../utils/helpers';
import { CAT_LABELS, fmtIn, parseBRL, fmtBRLInput } from '../utils/payments';

// Invoices (fornecedor/frete) e numerários (despachante) do pedido, na aba de
// Pagamentos. Cada documento tem o arquivo e, opcionalmente, nº, valor, datas e
// o pagamento que o quitou.
export const DOC_TYPES = {
  invoice:   { label: 'Invoice',   icon: '🧾', color: '#2B6CB0', bg: '#EBF4FF' },
  numerario: { label: 'Numerário', icon: '📑', color: '#B7791F', bg: '#FFFAF0' },
  outro:     { label: 'Outro',     icon: '📎', color: 'var(--gray-600)', bg: 'var(--gray-100)' },
};
const EMPTY = { doc_type: 'invoice', doc_number: '', amount: '', currency: '', issue_date: '', due_date: '', payment_id: '', notes: '' };
const defaultCurrency = (type, q) => (type === 'numerario' ? 'BRL' : (q.currency || 'USD'));

export function paymentLabel(p) {
  if (!p) return '';
  const cats = (p.allocations && p.allocations.length ? p.allocations.map(a => a.category) : [p.category || 'fornecedor'])
    .map(c => CAT_LABELS[c] || c).join(', ');
  return `${fmtDate(p.payment_date)} · ${fmtIn(p.currency, p.amount_foreign)} · ${cats}`;
}

export default function FinancialDocs({ quotation: q, onRefresh, toast }) {
  const docs = q.financial_documents || [];
  const payments = q.payments || [];
  const [form, setForm] = useState(null);        // null = fechado; { ...campos, id? }
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  function openNew(type = 'invoice') {
    setFiles([]);
    setForm({ ...EMPTY, doc_type: type, currency: defaultCurrency(type, q) });
  }
  function openEdit(d) {
    setFiles([]);
    setForm({ id: d.id, doc_type: d.doc_type, doc_number: d.doc_number || '', amount: d.amount != null ? fmtBRLInput(d.amount) : '',
      currency: d.currency || defaultCurrency(d.doc_type, q), issue_date: d.issue_date || '', due_date: d.due_date || '',
      payment_id: d.payment_id || '', notes: d.notes || '', original_name: d.original_name });
  }

  async function save() {
    if (!form.id && !files.length) return toast?.('Anexe o arquivo do documento', 'error');
    const fields = {
      doc_type: form.doc_type, doc_number: form.doc_number, currency: form.currency,
      amount: String(form.amount).trim() === '' ? '' : String(parseBRL(form.amount)),
      issue_date: form.issue_date, due_date: form.due_date, payment_id: form.payment_id, notes: form.notes,
    };
    setSaving(true);
    try {
      if (form.id) await ordersApi.updateFinancialDoc(form.id, fields);
      else await ordersApi.addFinancialDocs(q.id, files, fields);
      toast?.(form.id ? 'Documento atualizado' : `${DOC_TYPES[form.doc_type].label} anexad${form.doc_type === 'invoice' ? 'a' : 'o'}`, 'success');
      setForm(null); setFiles([]);
      onRefresh?.();
    } catch (e) {
      toast?.(e.response?.status === 413 ? 'Arquivo grande demais para o servidor' : (e.response?.data?.error || 'Erro ao salvar documento'), 'error');
    } finally { setSaving(false); }
  }

  async function remove(d) {
    if (!window.confirm(`Excluir ${DOC_TYPES[d.doc_type]?.label || 'documento'}${d.doc_number ? ` nº ${d.doc_number}` : ''} (${d.original_name})?`)) return;
    try { await ordersApi.deleteFinancialDoc(d.id); onRefresh?.(); }
    catch (e) { toast?.(e.response?.data?.error || 'Erro ao excluir', 'error'); }
  }

  // Totais por tipo e moeda (só documentos com valor)
  const totals = {};
  docs.forEach(d => {
    if (d.amount == null) return;
    const k = `${d.doc_type}|${d.currency || 'USD'}`;
    totals[k] = (totals[k] || 0) + Number(d.amount);
  });
  const shown = filter === 'all' ? docs : docs.filter(d => d.doc_type === filter);
  const count = t => docs.filter(d => d.doc_type === t).length;
  const lbl = { fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 3 };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 20 }} data-testid="financial-docs">
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--pantone)', flex: 1 }}>🧾 Invoices e numerários</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['all', `Todos (${docs.length})`], ['invoice', `Invoices (${count('invoice')})`], ['numerario', `Numerários (${count('numerario')})`]].map(([k, t]) => (
            <button key={k} type="button" className={`btn btn-sm ${filter === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(k)} style={{ fontSize: 11 }}>{t}</button>
          ))}
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => openNew('invoice')}>＋ Invoice</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => openNew('numerario')}>＋ Numerário</button>
      </div>

      {form && (
        <div style={{ padding: 16, background: 'var(--accent-light, #FAF5F6)', borderBottom: '1px solid var(--gray-100)' }} data-testid="fin-doc-form">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <div>
              <label style={lbl}>Tipo</label>
              <select value={form.doc_type} aria-label="Tipo do documento"
                onChange={e => { const t = e.target.value; setForm(f => ({ ...f, doc_type: t, currency: f.amount === '' ? defaultCurrency(t, q) : f.currency })); }}>
                {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Número</label>
              <input value={form.doc_number} aria-label="Número do documento" placeholder={form.doc_type === 'numerario' ? 'Ex.: NUM-0123' : 'Ex.: PI-2026-077'} onChange={e => set('doc_number', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Valor</label>
              <div style={{ display: 'flex', gap: 4 }}>
                <select value={form.currency} aria-label="Moeda" onChange={e => set('currency', e.target.value)} style={{ width: 74 }}>
                  {['USD', 'RMB', 'BRL'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input value={form.amount} aria-label="Valor do documento" placeholder="0,00" onChange={e => set('amount', e.target.value)} style={{ flex: 1 }} />
              </div>
            </div>
            <div>
              <label style={lbl}>Emissão</label>
              <input type="date" value={form.issue_date} aria-label="Data de emissão" max={today} onChange={e => set('issue_date', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Vencimento</label>
              <input type="date" value={form.due_date} aria-label="Vencimento" onChange={e => set('due_date', e.target.value)} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={lbl}>Pago pelo pagamento</label>
              <select value={form.payment_id} aria-label="Pagamento vinculado" onChange={e => set('payment_id', e.target.value)}>
                <option value="">— Ainda não pago / sem vínculo —</option>
                {payments.map(p => <option key={p.id} value={p.id}>{paymentLabel(p)}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={lbl}>Observação</label>
              <input value={form.notes} aria-label="Observação" placeholder="Ex.: sinal 30%, frete marítimo, DI 26/0012345…" onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            {form.id ? (
              <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>📎 {form.original_name} <span style={{ color: 'var(--gray-400)' }}>(arquivo mantido)</span></span>
            ) : (
              <>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>📎 Escolher arquivo(s)</button>
                <input ref={fileRef} type="file" multiple accept=".pdf,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg,.webp,.xml" data-testid="fin-doc-file"
                  style={{ display: 'none' }} onChange={e => { setFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
                <span style={{ fontSize: 12, color: files.length ? 'var(--gray-700)' : 'var(--gray-400)' }}>
                  {files.length ? files.map(f => f.name).join(', ') : 'Nenhum arquivo escolhido (PDF, Excel, imagem ou XML)'}
                </span>
              </>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setForm(null); setFiles([]); }} disabled={saving}>Cancelar</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? '⏳ Salvando…' : (form.id ? '💾 Salvar' : '📎 Anexar')}</button>
          </div>
        </div>
      )}

      <table className="table">
        <thead>
          <tr>
            <th>Tipo</th><th>Número</th><th style={{ textAlign: 'right' }}>Valor</th><th>Emissão</th><th>Vencimento</th>
            <th>Pagamento</th><th>Arquivo</th><th style={{ width: 80 }}></th>
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 20, fontSize: 13 }}>
              Nenhum documento. Use “＋ Invoice” ou “＋ Numerário” para anexar.
            </td></tr>
          )}
          {shown.map(d => {
            const t = DOC_TYPES[d.doc_type] || DOC_TYPES.outro;
            const overdue = !d.payment_id && d.due_date && d.due_date < today;
            const pay = payments.find(p => p.id === d.payment_id);
            return (
              <tr key={d.id} data-testid="fin-doc-row">
                <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: t.bg, color: t.color, whiteSpace: 'nowrap' }}>{t.icon} {t.label}</span></td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{d.doc_number || '—'}{d.notes && <div style={{ fontFamily: 'var(--font)', fontSize: 11, color: 'var(--gray-400)' }}>{d.notes}</div>}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{d.amount != null ? fmtIn(d.currency, d.amount) : '—'}</td>
                <td style={{ fontSize: 12 }}>{d.issue_date ? fmtDate(d.issue_date) : '—'}</td>
                <td style={{ fontSize: 12, color: overdue ? '#C53030' : 'inherit', fontWeight: overdue ? 700 : 400 }}>
                  {d.due_date ? fmtDate(d.due_date) : '—'}{overdue && <div style={{ fontSize: 10 }}>vencido, sem pagamento</div>}
                </td>
                <td style={{ fontSize: 12 }}>
                  {pay ? <span style={{ color: '#276749' }}>✓ {fmtDate(pay.payment_date)} · {fmtIn(pay.currency, pay.amount_foreign)}</span>
                       : <span style={{ color: 'var(--gray-400)' }}>—</span>}
                </td>
                <td><a href={`/uploads/${d.filename}`} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)', fontSize: 12 }}>📎 {d.original_name || 'arquivo'}</a></td>
                <td>
                  <div style={{ display: 'flex', gap: 2 }}>
                    <button type="button" className="btn btn-ghost btn-sm btn-icon" title="Editar dados" onClick={() => openEdit(d)}>✏️</button>
                    <button type="button" className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--error)' }} title="Excluir documento" onClick={() => remove(d)}>🗑️</button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {Object.keys(totals).length > 0 && (
        <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--gray-600)', display: 'flex', gap: 16, flexWrap: 'wrap', borderTop: '1px solid var(--gray-100)' }}>
          {Object.entries(totals).map(([k, v]) => {
            const [type, cur] = k.split('|');
            return <span key={k}><strong>{DOC_TYPES[type]?.label || type}s:</strong> {fmtIn(cur, v)}</span>;
          })}
        </div>
      )}
    </div>
  );
}
